/**
 * recoverFromCorruptedState.ts — 壊れた永続状態(IndexedDB等)での login 全滅ロックアウトの自己修復。
 *
 * ★背景(2026-08-24 実機確定): iOS Safari/WebKit のサンドボックスに壊れた永続 Firebase Auth 状態が
 *   溜まると、Firebase Auth の init が hang(onAuthStateChanged が発火せず)→ トークンが出ず →
 *   Firestore の全 read がトークン待ちで永久 hang → 法人も個人も表紙も全滅。plain reload では直らない
 *   (壊れた箱が残るため)。portal では PC でも同症状。👑実機で「サイトデータ削除で復帰」を実証済み。
 *
 * ★portal が失敗した罠 = "起動中に消す": `indexedDB.deleteDatabase` は開いている接続があると blocked
 *   で完了しない(web 仕様・全ブラウザ)。アプリ起動後(Firebase が箱を開いている)に消そうとしても
 *   blocked → 消せず → 同じ壊れた箱に戻る → 押しても直らないぐるぐる。
 *
 * ★この実装 = delete-before-init: 復旧ボタンは「フラグを立てて reload」するだけ(その場では消さない)。
 *   実際の削除は index.tsx の boot で firebase/config を import する"前"に runPendingRecovery() で行う。
 *   その時点ではまだ誰も IndexedDB を開いていない = deleteDatabase が blocked されず確実に消える。
 *
 * ★このモジュールは firebase を一切 import しない(生の indexedDB/localStorage/sessionStorage のみ)。
 *   だから firebase/config より前に安全に評価・実行できる。全処理は try/catch で握り潰し=boot を止めない。
 *
 * ★happy path 不変: 復旧フラグが無ければ runPendingRecovery() は即 false で返り、削除も何もしない。
 */

/** 復旧を要求した印(localStorage・reload を跨ぐ)。boot で検知して削除→即消す。 */
const PENDING_KEY = 'weborder:recovery:pending';
/** loop 防止: 同一タブセッションでの復旧試行回数(sessionStorage=タブ閉じで消える)。 */
const COUNT_KEY = 'weborder:recovery:count';
/** これ以上の自動復旧はせず手動案内に切替える閾値。 */
const EXHAUST_THRESHOLD = 2;

/** Firebase が作る内部 IndexedDB(削除しても SDK が自動再生成=安全)。databases() 不可時の fallback。 */
const FIREBASE_DB_NAMES = [
  'firebaseLocalStorageDb',
  'firebase-installations-database',
  'firebase-heartbeat-database',
];

export interface RecoveryResult {
  ran: boolean;
  dbsCleared: string[];
  keysCleared: string[];
}

/**
 * 復旧ボタンから呼ぶ。★その場では消さない(起動中削除は blocked される=portal の罠)。
 * フラグを立てて reload するだけ。実削除は次回 boot の runPendingRecovery() が担う。
 */
export function requestRecovery(): void {
  try {
    const prev = Number(sessionStorage.getItem(COUNT_KEY) || '0');
    sessionStorage.setItem(COUNT_KEY, String(prev + 1));
  } catch {
    /* sessionStorage 不可でも続行 */
  }
  try {
    localStorage.setItem(PENDING_KEY, String(Date.now()));
  } catch {
    /* localStorage 不可でも reload は試す */
  }
  try {
    window.location.reload();
  } catch {
    /* ignore */
  }
}

/** 復旧を既に何度も試して直らない=自動復旧を諦め手動案内に切替えるべきか。 */
export function isRecoveryExhausted(): boolean {
  try {
    return Number(sessionStorage.getItem(COUNT_KEY) || '0') >= EXHAUST_THRESHOLD;
  } catch {
    return false;
  }
}

/**
 * boot で firebase/config を読み込む"前"に呼ぶ。復旧フラグが立っていれば、壊れた Firebase の
 * 永続状態(IndexedDB + auth localStorage)を削除する。まだ誰も箱を開いていないので確実に消える。
 * 返り値: 実行したか(happy path=フラグ無し=false・削除なし)。全処理は握り潰し=throw しない。
 */
export async function runPendingRecovery(): Promise<RecoveryResult> {
  const result: RecoveryResult = { ran: false, dbsCleared: [], keysCleared: [] };

  let pending = false;
  try {
    pending = !!localStorage.getItem(PENDING_KEY);
  } catch {
    pending = false;
  }
  if (!pending) return result;
  result.ran = true;

  // フラグは即消す(削除が blocked 等で失敗しても、無限に削除ループしない)。
  try {
    localStorage.removeItem(PENDING_KEY);
  } catch {
    /* ignore */
  }

  // 1) Firebase 内部 IndexedDB を削除(この時点で開いている接続は無い=blocked されない)。
  const names = await listDbNames();
  await Promise.all(
    names.map(async (name) => {
      const ok = await deleteDbBounded(name, 3000);
      if (ok) result.dbsCleared.push(name);
    }),
  );

  // 2) Firebase auth + weborder のセッション状態を localStorage から掃除(綺麗な再ログインを強制)。
  //    ★注文下書き等の顧客重要データは localStorage/IndexedDB に無い(Firestore memory cache のみ)
  //      ため、この掃除でデータ喪失は起きない。再ログインは要るが無限ロックアウトより遥かにマシ。
  try {
    const toRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && (k.indexOf('firebase:authUser:') === 0 || k.indexOf('weborder:') === 0)) {
        toRemove.push(k);
      }
    }
    for (const k of toRemove) {
      try {
        localStorage.removeItem(k);
        result.keysCleared.push(k);
      } catch {
        /* 個別失敗は無視して続行 */
      }
    }
  } catch {
    /* ignore */
  }

  return result;
}

/** 削除対象の IndexedDB 名を列挙(databases() 使用可なら実在DB + 既知Firebase DB、不可なら既知のみ)。 */
async function listDbNames(): Promise<string[]> {
  try {
    const idb: any = typeof indexedDB !== 'undefined' ? indexedDB : undefined;
    if (idb && typeof idb.databases === 'function') {
      const list = await idb.databases();
      const found = (list || [])
        .map((d: any) => d && d.name)
        .filter((n: any): n is string => typeof n === 'string' && n.length > 0);
      const set = new Set<string>([...found, ...FIREBASE_DB_NAMES]);
      return Array.from(set);
    }
  } catch {
    /* fall through */
  }
  return [...FIREBASE_DB_NAMES];
}

/** deleteDatabase を per-DB timeout 付きで実行(blocked/hang で boot を止めない)。成功可否を返す。 */
function deleteDbBounded(name: string, timeoutMs: number): Promise<boolean> {
  return new Promise<boolean>((resolve) => {
    let done = false;
    const fin = (ok: boolean) => {
      if (done) return;
      done = true;
      resolve(ok);
    };
    let timer: ReturnType<typeof setTimeout> | undefined;
    try {
      if (typeof indexedDB === 'undefined') return fin(false);
      timer = setTimeout(() => fin(false), timeoutMs);
      const req = indexedDB.deleteDatabase(name);
      req.onsuccess = () => {
        if (timer) clearTimeout(timer);
        fin(true);
      };
      req.onerror = () => {
        if (timer) clearTimeout(timer);
        fin(false);
      };
      // onblocked: 開いている接続があり削除保留。boot前は起きない想定。起きても timeout で先へ進む。
      req.onblocked = () => {
        /* leave timeout to resolve(false); boot continues */
      };
    } catch {
      if (timer) clearTimeout(timer);
      fin(false);
    }
  });
}
